import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { Loader } from 'common/react/components/Loader';
import {
    playerStatsDataTypeSelector,
    playerStatsNavSelector,
    playerStatsCustomFieldsSelector
} from 'store/modules/mc-player-stats/selectors';
import { EmptyState } from 'common/react/components/EmptyState';
import { Table } from './components/Table';
import { composeRowData, composeColumnData } from './utils';
import { useDataType } from 'widgets/statspro/js/components/common/hooks/use-data-type';
import { updateContainerDataType } from './utils/update-container-data-type';
import { setDataType } from 'common/store/modules/mc-player-stats/init';
import {
    useGetMatchPlayerStatsQuery,
    useGetMatchSeasonStatsQuery
} from 'common/store/modules/api/cfs-api';

const POLL_DELAY = PULSE.app.common.CONSTANTS.MATCH_POLL_DELAY;

export const PlayerStatsTable = ({
    matchId,
    homePid,
    awayPid,
    homeNickname,
    awayNickname,
    compPid,
    status
}) => {
    const [poll, setPoll] = useState(POLL_DELAY);

    useEffect(() => {
        status !== PULSE.app.common.CONSTANTS.MATCH_STATUS.LIVE
            ? setPoll(0)
            : setPoll(POLL_DELAY);
    }, [status]);

    const {
        data: matchPlayers,
        isSuccess: matchPlayersIsSuccess,
        isLoading: matchPlayersIsLoading
    } = useGetMatchPlayerStatsQuery(
        { matchId },
        {
            pollingInterval: poll,
            skipPollingIfUnfocused: true
        }
    );

    const {
        data: matchAverages,
        isSuccess: matchAveragesIsSuccess,
        isFetching: matchAveragesIsFetching
    } = useGetMatchSeasonStatsQuery({
        competitionId: compPid,
        teamIds: [homePid, awayPid]
    });

    const matchData = useMemo(() => {
        return {
            ...matchPlayers,
            ...matchAverages
        };
    }, [matchPlayers, matchAverages]);

    const nav = useSelector(playerStatsNavSelector);
    const initialDataType = useSelector(playerStatsDataTypeSelector);
    const { dataType, updateDataType } = useDataType({
        initialDataType,
        onUpdateActionCreator: setDataType.request
    });
    const [rowData, setRowData] = useState([]);
    const [noDataAvailable, setNoDataAvailable] = useState(false);
    const [noPlayerData, setNoPlayerData] = useState(false);

    const homeAbbr = PULSE.app.common.team.getTeamAbbr(
        matchData?.playerAverages?.find((item) => item.team.teamId === homePid)
            ?.team?.teamId ||
            matchData?.players?.find((item) => item.teamId === homePid)?.teamId
    );

    const awayAbbr = PULSE.app.common.team.getTeamAbbr(
        matchData?.playerAverages?.find((item) => item.team.teamId === awayPid)
            ?.team?.teamId ||
            matchData?.players?.find((item) => item.teamId === awayPid)?.teamId
    );

    const customFields = useSelector(playerStatsCustomFieldsSelector);

    useEffect(() => {
        // Update the data with nav values when requests have completed
        setNoDataAvailable(false);

        if (matchPlayersIsSuccess && matchAveragesIsSuccess && homeAbbr) {
            let playersData = matchData.players ?? [];
            if (playersData.length) {
                // Team filters
                if (nav.teams === homeNickname) {
                    playersData = matchData.players.filter(
                        (item) => item.teamId === homePid
                    );
                } else if (nav.teams === awayNickname) {
                    playersData = matchData.players.filter(
                        (item) => item.teamId === awayPid
                    );
                }

                // Set data
                if (dataType.type === 'totals') {
                    if (!noPlayerData) {
                        updateDataType('totals');
                        setRowData(playersData);
                    }
                } else if (dataType.type === 'averages') {
                    if (matchData.playerAverages?.length) {
                        setRowData(
                            matchData.playerAverages?.filter((item) =>
                                playersData.find(
                                    (playerItem) =>
                                        playerItem.player.player.player
                                            .playerId === item.player.playerId
                                )
                            )
                        );
                    } else {
                        setNoDataAvailable(true);
                    }
                }
            } else if (matchData.playerAverages?.length) {
                // When there is no player data yet for upcoming matches,
                // set the initial data to use season averages
                if (dataType.type === 'totals') {
                    if (!noPlayerData) {
                        updateDataType('averages');
                        setNoPlayerData(true);
                    } else {
                        setNoDataAvailable(true);
                    }
                } else {
                    setRowData(
                        nav.teams === 'Both'
                            ? matchData.playerAverages
                            : matchData.playerAverages.filter(
                                  (item) => item.team.teamNickname === nav.teams
                              )
                    );
                }
            }
        } else {
            // When everything else fails, setNoDataAvailable to true
            // to prevent an infinity loader
            setNoDataAvailable(true);
        }
    }, [
        matchData,
        dataType.type,
        nav.stats,
        nav.teams,
        homeAbbr,
        matchPlayersIsSuccess,
        matchAveragesIsSuccess
    ]);

    useEffect(() => {
        updateContainerDataType(nav.stats);
    }, [nav.stats]);

    /**
     * Get columns.
     */
    const columns = useMemo(
        () => composeColumnData(nav.stats, customFields),
        [nav.stats, rowData, customFields]
    );

    /**
     * Get rows.
     */
    const data = useMemo(
        () =>
            rowData.map((item) =>
                composeRowData(
                    item,
                    columns.filter(
                        (col) =>
                            !['jumperNumber', 'player', 'empty'].includes(
                                col.accessor
                            )
                    ),
                    dataType.type,
                    noPlayerData,
                    homeAbbr,
                    awayAbbr,
                    homePid
                )
            ),
        [columns]
    );

    if (matchPlayersIsLoading || matchAveragesIsFetching) {
        // Loader for waiting for data
        return (
            <div className="mc-player-stats-table__loader">
                <Loader />
            </div>
        );
    } else if (noDataAvailable) {
        // Error handling for when there is no data
        return (
            <div className="mc-player-stats-table__empty-state">
                <EmptyState
                    titleTranslation="label.stats.error.noStatsAvailable"
                    summaryTranslation="label.stats.error.noStatsAvailable.summary"
                />
            </div>
        );
    }

    return (
        <Table
            key={nav.stats + customFields.length}
            columns={columns}
            data={data}
            modifier="player-stats"
            compPid={compPid}
            matchId={matchId}
        />
    );
};

PlayerStatsTable.propTypes = {
    matchId: PropTypes.string.isRequired,
    homePid: PropTypes.string.isRequired,
    awayPid: PropTypes.string.isRequired,
    homeNickname: PropTypes.string.isRequired,
    awayNickname: PropTypes.string.isRequired,
    compPid: PropTypes.string,
    status: PropTypes.string
};
